import { View } from 'backbone.marionette';
import { Collection } from 'backbone';
import _ from 'lodash';

import template from './templates/index.jst';
import Radio from 'backbone.radio';

import './index.styl';
import '../../../styles/squads.styl';
import { User } from '../../../models';
import { isAuthorized, Preloader } from '../../../utils';
import GeneralStats from '../GeneralStats';
import FixturesStats from '../FixturesStats';
import MatchStats from '../MatchStats';
import OpponentsStats from "../OpponentsStats";
import VenuesStats from "../VenuesStats";

export class PlayerProfileControls extends View {
	get template() {
		return template;
	}

	get className() {
		return 'player-profile-page';
	}

	regions() {
		return {
			'player_stats': '#player-stats'
		};
	}

	ui() {
		return {
			'select_team': '.js-select-team',
			'select_player': '.js-select-player',
			'coach_locked': '.js-coach-locked',
			'change_stats': '.js-change-stats',
			'add_to_favourites': '.js-add-to-favourites'
		};
	}

	events() {
		return {
			'change @ui.select_team': 'selectTeam',
			'change @ui.select_player': 'selectPlayer',
			'click @ui.coach_locked': 'showCoachPopup',
			'click @ui.change_stats': 'changeStats',
			'click @ui.add_to_favourites': 'addToFavourites'
		};
	}

	async initialize() {
		this.positions = this.getOption('positions').toJSON();
		this.players = this.getOption('players');
		this.rounds = this.getOption('rounds');
		this.venues = this.getOption('venues');
		this.squads = this.getOption('squads');
		this.player_id = this.getOption('player_id');
		this.match_stat = this.getOption('match_stat');
		if (User.id) {
			this.favourites = this.getOption('favourites');
		}

		this.player = {};

		if (User.has_assistant_coach) {
			this.coach = this.getOption('coach');
			this.coach_stats = this.getOption('coach_stats');
		}

		this.setListeners();
		this.model.set({
			is_authorized: isAuthorized(),
			external_link: ''
		}, { silent: true });

		Preloader.show();
		await this.getJSON();
		Preloader.hide();

	}

	setListeners() {
		this.listenTo(this, 'data:requested', this.onDataRequested);
		this.listenTo(this, 'player:match:stat:requested', this.onPlayerDataRequested);
		this.listenTo(this.model, 'change', this.render);
		return this;
	}

	async getJSON() {
		Preloader.show();

		const requests = _.invokeMap(
			_.pick(
				this,
				'players',
				'squads',
				'rounds',
				'venues',
				'coach',
				'favourites'
			),
			'fetch'
		);

		await Promise.all(requests);

		Preloader.hide();

		this.trigger('data:requested');
	}

	async onDataRequested() {
		const
			actual_round = this.rounds.actual,
			squads = this.squads.toJSON(),
			venues = this.venues.toJSON(),
			positions = this.positions,
			rounds = this.rounds.toJSON(),
			positions_by_id = _.keyBy(positions, 'id'),
			squad_by_id = _.keyBy(squads, 'id'),
			venues_by_id = _.keyBy(venues, 'id'),
			last_completed_round = this.rounds.last_completed,
			is_preseason = this.rounds.is_preseason,
			last_round = this.rounds.points_round,
			coach = User.has_assistant_coach ? this.coach.toJSON() : false,
			actual_round_id = actual_round.get('id'),
			key = 'full_name',
			bye_rounds = this.rounds
				.filter(
					round => round.get('is_bye') && round.get('status') === 'scheduled'
				).map(round => round.toJSON());

		this.players.each(player => {
			player
				.setPositionNames({ positions_by_id, key })
				.setSquadName({ squad_by_id })
				.setStatsCostDividedByAvgPoints()
				.setOpponent({ squad_by_id, round: actual_round.toJSON() })
				.setVenue({ venues_by_id, round: actual_round.toJSON() })
				.setCostChanges({ round_id: last_completed_round.get('id') })
				.setStatsPointsLastRound({
					is_preseason, rounds, round_id: last_round.get('id')
				});

			if (User.has_assistant_coach) {
				player
					.setCoachStats({
						coach,
						actual_round_id,
					})
					.setByRound({ bye_rounds });
			}
		});

		this.setPlayer();
		await this.getPlayerData();
		this.setModelInfo()
			.showStats();

		return this;
	}

	onPlayerDataRequested() {
		const { rounds, venues, squads, match_stat, coach_stats } = this;

		this.player
			.setFixturesTabData({
				rounds,
				venues,
				squads,
				coach_stats
			})
			.setMatchStatsTabData({
				match_stat,
				rounds,
				venues,
				headings: this.match_stat.match_stats_headings
			});

		if (User.has_assistant_coach) {
			this.player.setOpponentsTabData({
				rounds,
				venues,
				squads,
				coach_stats
			});

			this.player.setVenuesTabData({
				rounds,
				venues,
				squads,
				coach_stats
			});
		}

		return this;
	}

	setPlayer() {
		if (this.player_id) {
			this.player = this.players.get(parseInt(this.player_id, 10));
		}
		else {
			this.player = this.players.randomPlayer;
			this.player_id = this.player.get('id');
		}

		return this;
	}

	setModelInfo() {
		const player = this.player.toJSON();
		const { squad_name, first_name, last_name } = player;
		// due to discrepancy on nrl site routing
		const squadNameUse = squad_name === 'Tigers' ? 'wests-tigers' : squad_name;

		const external_link = `${squadNameUse}/${first_name}-${last_name}`
			.replace(/\s+/g, '-')
			.replace('\'', '')
			.toLowerCase();
		const is_favourite = this.favourites ?
			this.favourites.isFavourite(this.player.get('id')) :
			false;
		this.model.set({
			external_link,
			player,
			players: this.sortPlayersByTeam(this.player.get('squad_id'), this.players.toJSON()),
			squads: this.squads.toJSON(),
			match_stat: this.match_stat.toJSON(),
			has_assistant_coach: User.has_assistant_coach,
			rounds: this.rounds,
			venues: this.venues,
			is_favourite,
		});

		return this;
	}

	async getPlayerData() {
		const requests = [
			this.match_stat
				.clear()
				.setPlayerId(this.player_id)
				.fetch()
		];

		if (User.has_assistant_coach) {
			requests.push(
				this.coach_stats
					.clear()
					.setCoachPlayerId(this.player_id)
					.fetch()
			)
		}

		await Promise.all(requests);

		this.trigger('player:match:stat:requested');

		return this;
	}

	showStats(stats = 'general-stats') {
		let player_stats = this.getRegion('player_stats');

		switch (stats) {
			case 'general-stats':
				player_stats.show(new GeneralStats({
					model: this.model
				}));
				break;

			case 'fixtures-stats':
				player_stats.show(new FixturesStats({
					model: this.model,
					fixtures: new Collection(this.player.get('fixtures')),
					upcoming_fixtures: new Collection(this.player.get('upcoming_fixtures'))
				}));
				break;

			case 'match-stats':
				player_stats.show(new MatchStats({
					match_stat: this.match_stat,
					model: this.model,
					collection: new Collection(this.player.get('player_match_stats'))
				}));
				break;

			case 'opponents-stats':
				player_stats.show(new OpponentsStats({
					collection: new Collection(this.player.get('opponents_tab'))
				}));
				break;

			case 'venues-stats':
				player_stats.show(new VenuesStats({
					collection: new Collection(this.player.get('venues_tab'))
				}));
				break;

			default:
				player_stats.show(new GeneralStats({
					model: this.model
				}));
				break;
		}

		return this;
	}

	changeStats(element) {
		this.getUI('change_stats').removeClass('active');
		let current_stats = $(element.currentTarget).addClass('active').data('stats');

		this.showStats(current_stats);
	}

	showCoachPopup(event) {
		event.stopPropagation();
		Radio.channel('coach').trigger('show:popup');
	}

	selectTeam(element) {
		this.getUI('select_player').html('');
		let team_id = $(element.currentTarget).val(),
			players = this.players.toJSON(),
			sorted_players = this.sortPlayersByTeam(team_id, players);

		this.getUI('select_player').append(`<option class="hidden">Select player</option>`);
		_.mapValues(sorted_players, player => {
			let name = `${player.first_name} ${player.last_name}`;
			this.getUI('select_player').append(`<option value="${player.id}">${name}</option>`);
		});
	}

	sortPlayersByTeam(team_id, players) {
		return _.pickBy(players,
			player => _.result(player, 'squad_id') === _.parseInt(team_id)
		);
	}

	async selectPlayer(element) {
		this.player_id = $(element.currentTarget).val();
		this.setPlayer();

		await this.getPlayerData();

		this.setModelInfo()
			.showStats();
	}

	addToFavourites(element) {
		this.favourites.toggle(this.player_id);
		$(element.currentTarget).toggleClass('active')
	}
}

export default PlayerProfileControls;